import React, { useState, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import { timesheetApi } from '@/services/timesheetApi';
import type { Project, CostCentre } from '@/types/timesheet';
import type { DataMode } from '@/contexts/DataSourceContext';

const PAGE_SIZE = 20;

export interface UsePaginatedProjectsParams {
  screenMode: DataMode;
  costCentres: CostCentre[];
  companyId?: string;
}

export interface UsePaginatedProjectsResult {
  paginatedProjects: Project[];
  projectsPage: number;
  projectsTotalCount: number;
  projectsLoading: boolean;
  projectsHasMore: boolean;
  projectsSearchQuery: string;
  isSearchingProjects: boolean;
  projectsRemainingCount: number;
  costCentresByProjectCode: Record<string, CostCentre[]>;
  loadingCostCentresForProject: Record<string, boolean>;
  loadInitialProjects: () => Promise<void>;
  loadMoreProjects: () => Promise<void>;
  handleProjectSearch: (query: string) => Promise<void>;
  fetchCostCentresForProject: (projectCode: string) => Promise<void>;
  /** Reset pagination and the cost-centre cache when the active company changes. */
  resetForCompanyChange: () => void;
  /** Clear the fetched cost-centre cache (e.g. when the week/timesheet changes). */
  clearFetchedCostCentresCache: () => void;
}

/**
 * Encapsulates the server-side paginated projects state (list, page, totals,
 * loading/search flags) together with the load/search logic backed by
 * `timesheetApi.getProjects`, plus the per-project cost-centre fetching cache.
 */
export function usePaginatedProjects({
  screenMode,
  costCentres,
  companyId,
}: UsePaginatedProjectsParams): UsePaginatedProjectsResult {
  const [paginatedProjects, setPaginatedProjects] = useState<Project[]>([]);
  const [projectsPage, setProjectsPage] = useState(1);
  const [projectsTotalCount, setProjectsTotalCount] = useState(0);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsHasMore, setProjectsHasMore] = useState(true);
  const [projectsSearchQuery, setProjectsSearchQuery] = useState('');
  const [isSearchingProjects, setIsSearchingProjects] = useState(false);

  // Track cost centres per project code for filtering
  // Key: project code, Value: array of cost centres for that project
  const [costCentresByProjectCode, setCostCentresByProjectCode] = useState<Record<string, CostCentre[]>>({});
  const [loadingCostCentresForProject, setLoadingCostCentresForProject] = useState<Record<string, boolean>>({});
  // Ref to track in-flight requests without causing re-renders/stale closures
  const loadingCostCentresRef = useRef<Record<string, boolean>>({});
  const fetchedCostCentresRef = useRef<Record<string, boolean>>({});

  // Function to fetch cost centres filtered by project code
  const fetchCostCentresForProject = React.useCallback(async (projectCode: string) => {
    // Skip only if currently loading (prevent duplicate concurrent requests)
    if (loadingCostCentresRef.current[projectCode]) {
      return;
    }

    // Skip if already fetched (prevent redundant requests on effect re-runs)
    if (fetchedCostCentresRef.current[projectCode]) {
      return;
    }

    // In demo mode, use all cost centres (no filtering)
    if (screenMode === 'demo') {
      setCostCentresByProjectCode(prev => ({
        ...prev,
        [projectCode]: costCentres
      }));
      return;
    }

    // In live mode, fetch filtered cost centres from the API
    loadingCostCentresRef.current[projectCode] = true;
    setLoadingCostCentresForProject(prev => ({ ...prev, [projectCode]: true }));
    try {
      const filteredCostCentres = await timesheetApi.getCostCentresByProjectCode(projectCode);
      setCostCentresByProjectCode(prev => ({
        ...prev,
        [projectCode]: filteredCostCentres
      }));
      fetchedCostCentresRef.current[projectCode] = true;
    } catch (error) {
      console.error(`Failed to fetch cost centres for project ${projectCode}:`, error);
      // Fall back to all cost centres on error
      setCostCentresByProjectCode(prev => ({
        ...prev,
        [projectCode]: costCentres
      }));
    } finally {
      loadingCostCentresRef.current[projectCode] = false;
      setLoadingCostCentresForProject(prev => ({ ...prev, [projectCode]: false }));
    }
  }, [screenMode, costCentres]);

  // Load initial page of projects when component mounts (live mode only)
  const loadInitialProjects = React.useCallback(async () => {
    if (screenMode === 'demo') {
      // In demo mode, use all projects from context
      return;
    }
    
    setProjectsLoading(true);
    try {
      const response = await timesheetApi.getProjects({ 
        page: 1, 
        pageSize: PAGE_SIZE, 
        status: 'active',
        companyId,
      });
      setPaginatedProjects(response.data);
      setProjectsTotalCount(response.pagination.totalItems);
      setProjectsHasMore(response.pagination.hasNextPage);
      setProjectsPage(1);
    } catch (error) {
      console.error('Failed to load projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setProjectsLoading(false);
    }
  }, [screenMode, companyId]);

  // Load more projects (pagination)
  const loadMoreProjects = React.useCallback(async () => {
    if (projectsLoading || !projectsHasMore || screenMode === 'demo') return;
    
    setProjectsLoading(true);
    try {
      const nextPage = projectsPage + 1;
      const response = await timesheetApi.getProjects({ 
        page: nextPage, 
        pageSize: PAGE_SIZE, 
        status: 'active',
        search: projectsSearchQuery || undefined,
        companyId,
      });
      setPaginatedProjects(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const newProjects = response.data.filter(p => !existingIds.has(p.id));
        return [...prev, ...newProjects];
      });
      setProjectsPage(nextPage);
      setProjectsHasMore(response.pagination.hasNextPage);
      setProjectsTotalCount(response.pagination.totalItems);
    } catch (error) {
      console.error('Failed to load more projects:', error);
      toast.error('Failed to load more projects');
    } finally {
      setProjectsLoading(false);
    }
  }, [projectsLoading, projectsHasMore, projectsPage, projectsSearchQuery, screenMode, companyId]);

  // Handle server-side search for projects
  const handleProjectSearch = React.useCallback(async (query: string) => {
    if (screenMode === 'demo') return;
    
    setProjectsSearchQuery(query);
    setIsSearchingProjects(true);
    
    try {
      const response = await timesheetApi.getProjects({ 
        page: 1, 
        pageSize: PAGE_SIZE, 
        status: 'active',
        search: query || undefined,
        companyId,
      });
      setPaginatedProjects(response.data);
      setProjectsTotalCount(response.pagination.totalItems);
      setProjectsHasMore(response.pagination.hasNextPage);
      setProjectsPage(1);
    } catch (error) {
      console.error('Failed to search projects:', error);
    } finally {
      setIsSearchingProjects(false);
    }
  }, [screenMode, companyId]);

  // Calculate remaining count for "Load More" button
  const projectsRemainingCount = useMemo(() => {
    return Math.max(0, projectsTotalCount - paginatedProjects.length);
  }, [projectsTotalCount, paginatedProjects.length]);

  const resetForCompanyChange = React.useCallback(() => {
    // Reset paginated projects state
    setPaginatedProjects([]);
    setProjectsPage(1);
    setProjectsTotalCount(0);
    setProjectsHasMore(true);
    setProjectsSearchQuery('');
    // Reset cost centres cache
    setCostCentresByProjectCode({});
    fetchedCostCentresRef.current = {};
  }, []);

  const clearFetchedCostCentresCache = React.useCallback(() => {
    fetchedCostCentresRef.current = {};
  }, []);

  return {
    paginatedProjects,
    projectsPage,
    projectsTotalCount,
    projectsLoading,
    projectsHasMore,
    projectsSearchQuery,
    isSearchingProjects,
    projectsRemainingCount,
    costCentresByProjectCode,
    loadingCostCentresForProject,
    loadInitialProjects,
    loadMoreProjects,
    handleProjectSearch,
    fetchCostCentresForProject,
    resetForCompanyChange,
    clearFetchedCostCentresCache,
  };
}
